/**
 * Avatar initials by CODE POINTS, never `str[0]`.
 *
 * `str[0]` on an emoji-leading display name yields a lone UTF-16 surrogate
 * (renders U+FFFD "�"; on cu it hydration-crashed as React #418 — c3e6bc879).
 * LIVE-REPRO on supply 2026-08-07: the test user's display name starts with an
 * emoji ("🧪 TEST — Ops Manager"); the naive slice split the surrogate pair and
 * the server/client "�T" mismatch regenerated the whole tree on every page.
 *
 * Rule: split into words, take the first LETTER/NUMBER code point of each word
 * (words with none — emoji, dashes — are skipped entirely), max two, uppercased.
 * "🧪 TEST — Ops Manager" → "TO"; "Shaq Kamal" → "SK"; "🧪" alone → undefined.
 */
export function initialsOf(name: string | null | undefined): string | undefined {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  const letters: string[] = [];
  for (const word of words) {
    // Array.from iterates CODE POINTS — an astral emoji stays one unit.
    const glyph = Array.from(word).find((ch) => /[\p{L}\p{N}]/u.test(ch));
    if (glyph !== undefined) letters.push(glyph.toUpperCase());
    if (letters.length === 2) break;
  }
  if (letters.length === 0) return undefined;
  return letters.join("");
}

/**
 * Display-name formatting belt for the SAME emoji-leading names this file
 * already has to reason about for initials: at least one real `user_profiles`
 * row has `display_name` = "🧪TEST — Outlet Manager" (no space after the
 * emoji) — a manual-entry variant of the pattern `initialsOf` already
 * handles, not a new one.
 *
 * Uses `Intl.Segmenter` (not `Array.from`) because the leading unit can be a
 * multi-code-point grapheme (skin-tone modifier, ZWJ sequence) — code-point
 * iteration would slice it and reintroduce exactly the surrogate-splitting
 * bug `initialsOf`'s docblock describes, just one function over.
 * Idempotent: a name that already has the space (or has none at all, or
 * starts with a letter/number) is returned unchanged.
 *
 * Review round 4: a literal inserted space character does NOT fix this
 * visually — emoji glyph metrics eat a following space, so "🧪TEST" and
 * "🧪 TEST" render near-identically tight in the user menu's identity row
 * (confirmed live from a captured screenshot, not guessed). The VISIBLE
 * fix is layout (a real flex gap, via `splitLeadingEmoji` below), not a
 * character. This function is NOT dead, though: SupplyUserMenu's trigger
 * still uses it for the `aria-label` string, a pure-text context with no
 * glyph rendering involved, where a character *is* the correct fix.
 */
export function ensureSpaceAfterLeadingEmoji(name: string): string {
  if (!name) return name;
  const graphemes = Array.from(
    new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(name),
    (s) => s.segment,
  );
  const first = graphemes[0];
  if (first === undefined || /[\p{L}\p{N}]/u.test(first)) return name;
  const rest = graphemes.slice(1).join("");
  if (rest.length === 0 || rest.startsWith(" ")) return name;
  return `${first} ${rest}`;
}

/**
 * Layout-based sibling to `ensureSpaceAfterLeadingEmoji`, for contexts that
 * render the name as real DOM (not a plain string): splits off a leading
 * non-letter/number grapheme (an emoji, in practice) from the rest of the
 * name, so the caller can put a real flex `gap` between them instead of
 * relying on a space CHARACTER — which emoji glyph metrics can visually
 * swallow regardless of how many are actually in the string (review round
 * 4's finding). `lead` is `null` when the name doesn't start with one (the
 * common case — most names have no leading emoji at all), so callers can
 * render `rest` alone with no wrapper. Any leading whitespace already in
 * `rest` is stripped, so this is safe to call on a name that already has a
 * (now-redundant, but harmless) space character too.
 */
export function splitLeadingEmoji(name: string): { lead: string | null; rest: string } {
  if (!name) return { lead: null, rest: name };
  const graphemes = Array.from(
    new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(name),
    (s) => s.segment,
  );
  const first = graphemes[0];
  if (first === undefined || /[\p{L}\p{N}]/u.test(first)) return { lead: null, rest: name };
  const rest = graphemes.slice(1).join("").replace(/^\s+/, "");
  return { lead: first, rest };
}
